import noop from 'lodash/noop';
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  use,
  useCallback,
  useMemo,
  useState,
} from 'react';

export enum NoteSize {
  Closed = 'CLOSED',
  Minimized = 'MINIMIZED',
  Open = 'OPEN',
  Maximized = 'MAXIMIZED',
}

type NotesContextType = {
  openNotesList: () => void;
  closeNotesList: () => void;
  notesListOpen: boolean;
  setNotesListOpen: Dispatch<SetStateAction<boolean>>;
  noteId: string | null | undefined;
  openNote: (options?: { noteId?: string | null }) => void;
  closeNote: () => void;
  setNoteSize: Dispatch<SetStateAction<NoteSize>>;
  noteSize: NoteSize;
  guidelinesOpen: boolean;
  setGuidelinesOpen: (open: boolean) => void;
  setNoteId: Dispatch<SetStateAction<string | null | undefined>>;
};

export const defaultNotesContextValues = {
  openNotesList: noop,
  closeNotesList: noop,
  notesListOpen: false,
  setNotesListOpen: noop,
  noteId: undefined,
  openNote: noop,
  closeNote: noop,
  setNoteSize: noop,
  setNoteId: noop,
  noteSize: NoteSize.Closed,
  guidelinesOpen: false,
  setGuidelinesOpen: noop,
};

const NotesContext = createContext<NotesContextType>(defaultNotesContextValues);

export const getNotesContextForTesting = () => {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('getNotesContextForTesting should only be used in tests');
  }
  return NotesContext;
};

interface NotesProviderProps extends PropsWithChildren {}

const NotesProvider: React.FC<NotesProviderProps> = ({ children }) => {
  const [notesListOpen, setNotesListOpen] = useState(false);
  const [noteSize, setNoteSize] = useState<NoteSize>(NoteSize.Closed);
  const [noteId, setNoteId] = useState<NotesContextType['noteId']>();
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  const openNotesList: NotesContextType['openNotesList'] = () => {
    setNotesListOpen(true);
  };

  const closeNotesList = () => {
    setNotesListOpen(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: eslint migration
  const openNote: NotesContextType['openNote'] = useCallback((options) => {
    setNoteId(options?.noteId);
    setNoteSize(NoteSize.Open);
    closeNotesList();
  }, []);

  const closeNote = () => {
    setNoteSize(NoteSize.Closed);
    setNoteId(undefined);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: eslint migration
  const memoizedValue = useMemo(
    () => ({
      openNotesList,
      closeNotesList,
      notesListOpen,
      setNotesListOpen,
      openNote,
      closeNote,
      setNoteSize,
      noteSize,
      noteId,
      guidelinesOpen,
      setGuidelinesOpen,
      setNoteId,
    }),
    [guidelinesOpen, noteId, noteSize, notesListOpen, openNote],
  );
  return (
    <NotesContext.Provider value={memoizedValue}>
      {children}
    </NotesContext.Provider>
  );
};

function useNotesContext(): NotesContextType {
  return use(NotesContext);
}

export { NotesProvider, useNotesContext };

interface TestNotesOpenerProps {}
export const TestNotesOpener: React.FC<TestNotesOpenerProps> = () => {
  const { openNotesList, notesListOpen, closeNotesList } = useNotesContext();

  if (process.env.NODE_ENV !== 'test') {
    throw new Error('TestNotesOpener should only be used in tests');
  }

  return (
    <>
      <button
        data-testid="test-notes-open-button"
        type="button"
        onClick={openNotesList}
      >
        open notes list
      </button>
      <button
        data-testid="test-notes-close-button"
        type="button"
        onClick={closeNotesList}
      >
        close notes list
      </button>
      <div data-testid="notes-list-open">{notesListOpen.toString()}</div>
    </>
  );
};

export const TestNoteOpener: React.FC = () => {
  const { openNote, closeNote, noteId, noteSize, notesListOpen } =
    useNotesContext();

  if (process.env.NODE_ENV !== 'test') {
    throw new Error('TestNoteOpener should only be used in tests');
  }

  return (
    <>
      <button
        data-testid="test-note-open-button"
        type="button"
        onClick={() => openNote({ noteId: 'myNoteId' })}
      >
        open-note
      </button>
      <button
        data-testid="test-note-close-button"
        type="button"
        onClick={() => closeNote()}
      >
        close-note
      </button>
      <div data-testid="note-id">{noteId}</div>
      <div data-testid="note-size">{noteSize}</div>
      <div data-testid="notes-list-open">{notesListOpen.toString()}</div>
    </>
  );
};
